import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../common/Layout';
import { Smile, Meh, Frown } from 'lucide-react';
import { interpretScore } from '@/utils/scoring';
import AnalysisDetailsDialog from '../photo/AnalysisDetailsDialog';
import { saveEvaluationResult } from '@/services/evaluationService';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import ScoreCard from '../recommendations/ScoreCard';
import { useTranslation } from 'react-i18next';

const ResultScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  useEffect(() => {
    // If there's no score in location state, try to get from localStorage
    if (!location.state || !location.state.score) {
      const storedScore = localStorage.getItem('lastAnemiaScore');
      if (!storedScore) {
        navigate('/', { replace: true });
      }
    }
  }, [location.state, navigate]);

  if (!location.state && !localStorage.getItem('lastAnemiaScore')) {
    return null;
  }
  
  // Get the score from the location state or localStorage
  const getStateValue = (key: string, localStorageKey?: string, defaultValue?: any) => {
    if (location.state && location.state[key] !== undefined) {
      return location.state[key];
    }
    if (localStorageKey && localStorage.getItem(localStorageKey)) {
      return localStorage.getItem(localStorageKey);
    }
    return defaultValue;
  };
  
  const score = getStateValue('score', 'lastAnemiaScore', 0);
  const interpretation = getStateValue('interpretation', 'lastAnemiaCategory', interpretScore(typeof score === 'string' ? parseInt(score) : score));
  const resultCategory = getStateValue('resultCategory', null, t('results.title'));
  const resultIconType = getStateValue('resultIconType', null, 'meh') as 'smile' | 'meh' | 'frown';
  const resultColor = getStateValue('resultColor', null, 'warning');
  const eyesAnalysis = getStateValue('eyesAnalysis');
  const nailsAnalysis = getStateValue('nailsAnalysis');
  const profileId = getStateValue('profileId');
  const userId = getStateValue('userId');
  const latitude = getStateValue('latitude');
  const longitude = getStateValue('longitude');

  // Check if the results came from photo analysis
  const hasPhotoAnalysis = eyesAnalysis || nailsAnalysis;
  const hasLocationData = latitude !== undefined && longitude !== undefined;
  
  // Save photo evaluation results to database only if logged in
  useEffect(() => {
    const saveResults = async () => {
      if (hasPhotoAnalysis && profileId && userId && profileId !== 'guest') {
        try {
          const { data } = await supabase.auth.getSession();
          if (!data.session) return;
          
          await saveEvaluationResult({
            score: typeof score === 'string' ? parseInt(score) : score,
            interpretation: interpretation,
            resultCategory: resultCategory,
            resultIconType: resultIconType,
            resultColor: resultColor,
            profileId: profileId,
            userId: userId,
            latitude: latitude,
            longitude: longitude,
            eyesAnalysis: eyesAnalysis,
            nailsAnalysis: nailsAnalysis
          });
          
          toast.success(t('results.saveSuccess'));
        } catch (error: any) {
          console.error('Error saving evaluation results:', error);
          toast.error(t('results.saveError', { error: error.message }));
        }
      }
    };
    
    saveResults();
  }, [hasPhotoAnalysis, profileId, userId]);

  // Always save score to localStorage for the profile screen
  localStorage.setItem('lastAnemiaScore', score.toString());
  localStorage.setItem('lastAnemiaCategory', interpretation);

  return (
    <Layout title={t('results.title')} showBackButton={true}>
      <div className="flex flex-col items-center pt-4 animate-fade-in">
        <div className="mb-6">
          <ScoreCard score={score} />
        </div>
        
        {hasPhotoAnalysis && (
          <AnalysisDetailsDialog 
            eyesAnalysis={eyesAnalysis} 
            nailsAnalysis={nailsAnalysis} 
          />
        )}
        
        <button 
          onClick={() => navigate('/recommendations', { 
            state: { score, resultCategory } 
          })}
          className="btn-primary w-full mb-4"
        >
          {t('results.nextSteps')}
        </button>
        
        <button 
          onClick={() => navigate('/')}
          className="btn-outline w-full"
        >
          {t('results.backToHome')}
        </button>
      </div>
    </Layout>
  );
};

export default ResultScreen;
